import { configureStore, isRejectedWithValue } from '@reduxjs/toolkit'
import authReducer from './authSlice'
import goalsReducer from './goalsSlice'
import userSettingsReducer from './userSettingsSlice'
import performanceReducer from './performanceSlice'
import tasksReducer from './tasksSlice'
import notificationReducer from './notificationSlice'
import taskAttachmentsReducer from './taskAttachmentsSlice'
import dailyCheckInsReducer from './dailyCheckInsSlice'
import bootstrapReducer from './bootstrapSlice'
import labelsReducer from './labelsSlice'
import loopsReducer from './loopsSlice'
import onboardingReducer from './onboardingSlice'
import uiReducer from './uiSlice'
import wikiReducer from './wikiSlice'
import { isNetworkError } from '../utils/networkUtils'

/**
 * Middleware to log rejected async thunk actions
 * Uses shared isNetworkError utility for consistent detection
 */
const rejectionHandlerMiddleware = () => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    if (isNetworkError(action.payload)) {
      // Network errors are expected during wake from sleep - log quietly
      console.log('[Redux] Network error in async action:', action.type)
    } else {
      // Non-network errors should be logged as warnings
      console.warn('[Redux] Async action rejected:', action.type, action.payload)
    }
  }
  return next(action)
}

export const store = configureStore({
  reducer: {
    auth: authReducer,
    goals: goalsReducer,
    userSettings: userSettingsReducer,
    performance: performanceReducer,
    tasks: tasksReducer,
    notifications: notificationReducer,
    taskAttachments: taskAttachmentsReducer,
    dailyCheckIns: dailyCheckInsReducer,
    bootstrap: bootstrapReducer,
    labels: labelsReducer,
    loops: loopsReducer,
    onboarding: onboardingReducer,
    ui: uiReducer,
    wiki: wikiReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['auth/initialize/fulfilled', 'auth/signIn/fulfilled', 'auth/signUp/fulfilled'],
        ignoredActionsPaths: ['payload.session', 'payload.user'],
        ignoredPaths: ['auth.session', 'auth.user'],
      },
    }).concat(rejectionHandlerMiddleware),
})

// Export types for use in TypeScript files if needed later
// export type RootState = ReturnType<typeof store.getState>
// export type AppDispatch = typeof store.dispatch
